import React, { createContext, useContext, useMemo, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { withNamespaces } from 'react-i18next';
import { Redirect, useLocation } from 'react-router-dom';
import { InlineFeedback, Skeleton, Surface, ThemeACompatibilityBoundary } from '../design-system/v2/feedback';
import { resolveZooProductScope } from './productScope';
import { canOpenProductLocation, canUseProductSection, isUnroutedLocation, productUnavailableMessage } from './productRoutePolicy';

const legacyScope = resolveZooProductScope(null);
export const ProductAccessContext = createContext(legacyScope);
export const useProductAccess = () => useContext(ProductAccessContext);
export function ProductAccessProvider({ zoo, userId, authReady, children }) {
  const parent = useContext(ProductAccessContext);
  const viewerId = userId ?? parent.userId;
  // Unknown until the first auth callback: a per-user Rafiki setting must not be evaluated with a null viewer.
  const viewerReady = authReady ?? parent.authReady ?? true;
  const scope = useMemo(() => ({ ...resolveZooProductScope(zoo, viewerId), userId: viewerId, authReady: viewerReady }), [zoo, viewerId, viewerReady]);
  return <ProductAccessContext.Provider value={scope}>{children}</ProductAccessContext.Provider>;
}
export function ReduxProductAccessProvider({ children }) {
  const zooOptions = useSelector((state) => state.zoo?.zooOptions);
  const zooType = useSelector((state) => state.zoo?.zooType);
  // La catégorie (Laboratoire) ferme les fonctions ZIMS : elle vit hors de zooOptions.
  const zoo = useMemo(() => (zooType ? { ...(zooOptions || {}), zooType } : zooOptions), [zooOptions, zooType]);
  const [auth, setAuth] = useState({ userId: null, ready: false });
  useEffect(() => {
    let disposed = false;
    let unsubscribe;
    import('../tools/conf').then(({ initAuth }) => {
      if (!disposed) unsubscribe = initAuth().onAuthStateChanged(user => setAuth({ userId: user?.uid || null, ready: true }));
    });
    return () => { disposed = true; unsubscribe?.(); };
  }, []);
  return <ProductAccessProvider zoo={zoo} userId={auth.userId} authReady={auth.ready}>{children}</ProductAccessProvider>;
}
// `location` (refus d'une route) : message propre à la fonction hors offre quand il existe.
export const ProductUnavailable = withNamespaces('translations')(({ t, location }) => {
  const specific = location ? productUnavailableMessage(location) : null;
  if (!specific) return <ThemeACompatibilityBoundary><Surface><InlineFeedback intent="information" title={t('productScope.unavailable')} description={t('productScope.unavailableHint')} /></Surface></ThemeACompatibilityBoundary>;
  return <ThemeACompatibilityBoundary><Surface><InlineFeedback intent="information" title={t(specific.titleKey)} description={t(specific.descriptionKey)}>{t('productScope.unavailableHint')}</InlineFeedback></Surface></ThemeACompatibilityBoundary>;
});
export function ProductFeature({ id, anyOf, children, fallback = null }) {
  const access = useProductAccess();
  return access.allows(anyOf || [id]) ? children : fallback;
}
export function ProductSection({ entity, section, subsection, children, fallback = <ProductUnavailable /> }) {
  const scope = useProductAccess();
  return canUseProductSection(scope, entity, section, subsection) ? children : fallback;
}
export function ProductRouteBoundary({ children }) {
  const scope = useProductAccess();
  const location = useLocation();
  if (canOpenProductLocation(scope, location)) return children;
  return isUnroutedLocation(location) ? <Redirect to="/DashboardNext" /> : <ProductUnavailable location={location} />;
}

export function LegacyProductBoundary({ children }) {
  const ready = useSelector((state) => state.zoo?.productScopeReady);
  const failed = useSelector((state) => state.zoo?.zooOptions?.scopeLoadFailed);
  const { authReady } = useProductAccess();
  if (failed) return <ProductUnavailable />;
  return ready && authReady !== false ? <ProductRouteBoundary>{children}</ProductRouteBoundary> : <ThemeACompatibilityBoundary><Skeleton variant="block" /></ThemeACompatibilityBoundary>;
}

export const withProductFeature = (id) => (Component) => {
  function ScopedProductView(props) {
    return <ProductFeature id={id}><Component {...props} /></ProductFeature>;
  }
  ScopedProductView.displayName = `ProductFeature(${Component.displayName || Component.name || id})`;
  return ScopedProductView;
};
